import { Component, OnInit, AfterViewInit, ViewChild  } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import {
  AppService, DCO, DentalBidirectionalRole, DentalBidirectionalUser,
  GetDCOsRequest, GetDCOsResponse,
  GetDentalBidirectionalRolesRequest,
  GetUsersRequest, GetUsersResponse,
  UpdateUserRequest, UpdateUserResponse
} from '../../../app.service';
import { AlertService } from '../../../common/alert.service';
import { LoadingService } from '../../../common/loading.service';
import { UserEditDialogComponent } from '../user-edit-dialog/user-edit-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';


const INTERNAL_DOMAIN = '@careoregon.org';



type UserTableFilter = {
  search: string;            // normalized search text
  dcoId: number | null;      // selected DCO id
  includeInactive: boolean;  // true = show inactive users as well
  filtersEnabled: boolean;   // master toggle
  roleIds: number[];
  roleMatch: 'any' | 'all';
  userType: 'all' | 'internal' | 'external';
};

@Component({
  selector: 'app-user-management-table',
  templateUrl: './user-management-table.component.html',
  styleUrls: ['./user-management-table.component.scss']
})
export class UserManagementTableComponent implements OnInit {

  @ViewChild(MatSort) sort!: MatSort;

  public users: DentalBidirectionalUser[] = [];
  public usersDataSource: MatTableDataSource<DentalBidirectionalUser> = new MatTableDataSource([]);
  public displayedColumns: string[] = ['firstName', 'lastName', 'emailAddress', 'dco', 'roles', 'userType', 'isActive', 'actions'];
  public availableRoles: DentalBidirectionalRole[] = [];
  public availableDcos: DCO[] = [];

  // 🔎 UI state for the actions bar
  public currentSearchValue = '';
  public currentFilterSelection: number | null = null; // DCO id
  public filtersEnabled = true;
  public showClosed = true;
  public selectedRoleIds: number[] = [];
  public roleMatch: 'any' | 'all' = 'any';
  public userType: 'all' | 'internal' | 'external' = 'all';

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private dialog: MatDialog
  ) {
    // Define a single filter predicate up-front
    this.usersDataSource.filterPredicate = (user: DentalBidirectionalUser, raw: string) => {
      let state: UserTableFilter;
      try {
        state = JSON.parse(raw) as UserTableFilter;
      } catch {
        // Fallback – no filtering
        return true;
      }

      // If master toggle is off, show everything
      if (!state.filtersEnabled) return true;

      // 1) Closed/Inactive filter
      if (!state.includeInactive && !user.isActive) return false;

      // 2) DCO filter (by id)
      if (state.dcoId != null) {
        const userDcoId = user.dentalCareOrganizationId ?? null;
        if (userDcoId !== state.dcoId) return false;
      }

      // 3) Roles filter
      if (state.roleIds?.length) {
        const userRoleIds = (user.roles ?? []).map(r => r.id);
        const hasAll = state.roleIds.every(id => userRoleIds.includes(id));
        const hasAny = state.roleIds.some(id => userRoleIds.includes(id));
        if (state.roleMatch === 'all' ? !hasAll : !hasAny) return false;
      }

      // 4) Internal/External filter by email domain
      if (state.userType !== 'all') {
        const email = (user.emailAddress ?? '').toLowerCase().trim();
        const isInternal = this.isInternalEmail(email);
        if (state.userType === 'internal' && !isInternal) return false;
        if (state.userType === 'external' && isInternal) return false;
      }


      // 5) Search filter (min 3 chars)
      const s = (state.search || '').trim();
      if (s.length >= 3) {
        const haystack = this.normalize([
          user.firstName,
          user.lastName,
          user.emailAddress,
          user.dentalCareOrganizationDisplayName,
          ...(user.roles?.map(r => r.name) ?? [])
        ].filter(Boolean).join(' '));

        return haystack.includes(s);
      }

      return true;
    };
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadAvailableRoles();
    this.loadAvailableDcos();
  }

  ngAfterViewInit(): void {
    // Attach sort after view is ready
    this.usersDataSource.sort = this.sort;

    // Case-insensitive & null-safe value extraction per column
    this.usersDataSource.sortingDataAccessor = (user, columnId) => {
      switch (columnId) {
        case 'firstName':
          return this.toKey(user.firstName);
        case 'lastName':
          return this.toKey(user.lastName);
        case 'emailAddress':
          return this.toKey(user.emailAddress);
        case 'dco':
          return this.toKey(user.dentalCareOrganizationDisplayName);
        case 'roles':
          return this.toKey((user.roles ?? []).map(r => r.name).join(', '));
        case 'userType':
          return this.isInternalEmail(user.emailAddress) ? 'internal' : 'external';
        case 'isActive':
          // Sort inactive (false) before active (true) by default
          return user.isActive ? 1 : 0;
        default:
          return '';
      }
    };

    this.usersDataSource.sortData = (data, sort) => {
      const isAsc = sort.direction === 'asc';
      const column = sort.active;

      // If no active sort, return as-is
      if (!column || !sort.direction) return data;

      return [...data].sort((a, b) => {
        const va = this.usersDataSource.sortingDataAccessor(a, column);
        const vb = this.usersDataSource.sortingDataAccessor(b, column);

        // numbers (isActive) vs strings
        if (typeof va === 'number' && typeof vb === 'number') {
          return (va - vb) * (isAsc ? 1 : -1);
        }

        return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' }) * (isAsc ? 1 : -1);
      });
    };

    this.sort.active = 'firstName';
    this.sort.direction = 'asc';
    this.sort.sortChange.emit();
  }


  // ---------- Data load ----------
  private loadUsers(): void {
    this.getUsers().subscribe(() => this.applyFilter());
  }

  private getUsers(): Observable<DentalBidirectionalUser[]> {
    const request = new GetUsersRequest();
    this.spinner.show();

    return this.service.GetUsers(request).pipe(
      map((response: GetUsersResponse) => {
        if (response.success) {
          this.users = response.users ?? [];

          this.users.sort((a, b) =>
            (a.firstName ?? '').localeCompare(b.firstName ?? '', undefined, { sensitivity: 'base' })
          );
          this.usersDataSource.data = this.users;

          // ensure sort is still applied after data refresh
          if (this.sort) this.usersDataSource.sort = this.sort;

          return this.users;
        } else {
          console.error('Failed to load users:', response.message);
          return [];
        }
      }),
      catchError(err => {
        console.error('Error during GetUsers:', err);
        return of([]);
      }),
      finalize(() => this.spinner.hide())
    );
  }

  private loadAvailableRoles(): void {
    this.spinner.show();
    const request = new GetDentalBidirectionalRolesRequest();

    this.service.GetDentalBidirectionalRoles(request).pipe(
      map(response => {
        if (response.success) {
          this.availableRoles = response.roles ?? [];
        } else {
          console.error(`Failed to load roles: ${response.message}`);
          this.availableRoles = [];
        }
      }),
      catchError(error => {
        console.error('Error loading roles', error);
        this.availableRoles = [];
        return of(null);
      }),
      finalize(() => this.spinner.hide())
    ).subscribe();
  }

  private loadAvailableDcos(): void {
    const request = new GetDCOsRequest();
    request.activeOnly = true;

    this.spinner.show();
    this.service.GetDCOs(request).pipe(
      map((result: GetDCOsResponse) => {
        if (result.success) {
          this.availableDcos = result.dcos ?? [];
        } else {
          console.log(`ERROR: ${result.message}`);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
      finalize(() => this.spinner.hide())
    ).subscribe();
  }

  // ---------- Dialog ----------
  public editUser(user: DentalBidirectionalUser): void {
    const dialogRef = this.dialog.open(UserEditDialogComponent, {
      width: '500px',
      data: { user, roles: this.availableRoles, dcos: this.availableDcos }
    });

    dialogRef.afterClosed().subscribe((result: DentalBidirectionalUser | undefined) => {
      if (result) {
        const updateRequest = new UpdateUserRequest();
        updateRequest.user = result;

        this.spinner.show();
        this.service.UpdateUser(updateRequest).pipe(
          tap((response: UpdateUserResponse) => {
            if (response.success) {
              const index = this.users.findIndex(u => u.userId === response.user.userId);
              if (index !== -1) {
                this.users[index] = response.user;
                this.usersDataSource.data = [...this.users];
                this.applyFilter(); // keep filters applied after update
              }
            } else {
              console.error('Update failed:', response.message);
            }
          }),
          catchError(err => {
            console.error('Update error:', err);
            return of(null);
          }),
          finalize(() => this.spinner.hide())
        ).subscribe();
      }
    });
  }

  // ---------- Actions bar handlers ----------
  onSearch(): void {
    // live-update as user types
    this.applyFilter();
  }

  clearSearch(): void {
    this.currentSearchValue = '';
    this.applyFilter();
  }

  onFilterChange(): void {
    this.applyFilter();
  }

  clearFilter(): void {
    this.currentFilterSelection = null;
    this.applyFilter();
  }

  resetAllFilters(): void {
    this.currentSearchValue = '';
    this.currentFilterSelection = null;
    this.filtersEnabled = true;
    this.showClosed = true;
    this.selectedRoleIds = [];
    this.roleMatch = 'any';
    this.userType = 'all';
    this.applyFilter();

  }

  hasActiveFilters(): boolean {
    const hasSearch = !!this.currentSearchValue?.trim() && this.currentSearchValue.trim().length >= 3;
    const hasDco = this.currentFilterSelection != null;         // note: NOT just truthy
    const hasRoles = (this.selectedRoleIds?.length ?? 0) > 0;
    const hasUserType = this.userType !== 'all';                // <-- include this
    return hasSearch || hasDco || hasRoles || hasUserType;
  }


  displayFilterName(dcoId: number | null): string {
    if (dcoId == null) return '';
    const dco = this.availableDcos.find(d => d.id === dcoId);
    return dco ? dco.displayName ?? dco.name ?? `DCO #${dco.id}` : `DCO #${dcoId}`;
  }

  roleNamesByIds(ids: number[]): string {
    const map = new Map(this.availableRoles.map(r => [r.id, r.name]));
    return ids.map(id => map.get(id) ?? `Role #${id}`).join(', ');
  }


  toggleShowClosed(): void {
    // In this context, "Show Closed" == include inactive
    this.showClosed = !this.showClosed;
    this.applyFilter();
  }

  onRolesFilterChange(): void {
    this.applyFilter();
  }

  clearRolesFilter(): void {
    this.selectedRoleIds = [];
    this.applyFilter();
  }

  onUserTypeChange(): void {
    this.applyFilter();
  }


  // ---------- Core filter application ----------
  applyFilter(): void {
    const state: UserTableFilter = {
        search: this.normalize(this.currentSearchValue),
        dcoId: this.currentFilterSelection ?? null,
        includeInactive: this.showClosed,
        filtersEnabled: this.filtersEnabled,
        roleIds: this.selectedRoleIds,
        roleMatch: this.roleMatch,
        userType: this.userType
    };

    // MatTableDataSource.filter must be a string,
    // so we serialize our state object.
    this.usersDataSource.filter = JSON.stringify(state);

    // reset paginator to first page (paginator not yet implemented)
    // this.paginator?.firstPage();
  }

  // ---------- Utilities ----------
  private normalize(v?: string): string {
    return (v ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim();
  }

  // Utility for sorting keys
  private toKey(v?: string | null): string {
    return (v ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
  }

  getRoleIcon(roleName: string): string {
    switch (roleName.toLowerCase()) {
      case 'admin': return 'fa-solid fa-gear';
      case 'superadmin': return 'fa-solid fa-crown';
      case 'reviewer': return 'fa-solid fa-eye';
      case 'provideruser': return 'fa-solid fa-stethoscope';
      case 'dcouser': return 'fa-solid fa-tooth';
      default: return 'fa-solid fa-user';
    }
  }

  public isInternalEmail(email: string | null | undefined): boolean {
    if (!email) return false;
    return email.trim().toLowerCase().endsWith(INTERNAL_DOMAIN);
  }
}
